import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as OutcomesIcon,
  People as RosterIcon,
  Timeline as PlanningIcon,
  TrendingUp as ForecastIcon,
  AdminPanelSettings as AdminIcon,
  BugReport as TestIcon,
  ExpandMore as ExpandMoreIcon,
  DeveloperMode as DeveloperModeIcon,
  Folder as FolderIcon,
  GroupWork as CapacityIcon,
  Map as RoadmapsIcon,
  AccountTree as DataMapIcon,
} from '@mui/icons-material';

function SectionHeader({ label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontWeight: 700, letterSpacing: 1.2, lineHeight: 1, whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
    </Box>
  );
}

function ModuleGrid({ modules, isCompact }) {
  return (
    <Grid container spacing={isCompact ? 1 : 3}>
      {modules.map((module) => {
        const IconComponent = module.icon;
        const isDisabled = module.disabled || false;
        return (
          <Grid
            item
            xs={isCompact ? 6 : 12}
            sm={isCompact ? 4 : 6}
            md={isCompact ? 2 : 4}
            lg={isCompact ? 2 : 3}
            key={`${module.id}-${module.subModule || module.id}`}
          >
            <Card
              elevation={2}
              sx={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease-in-out',
                opacity: isDisabled ? 0.5 : 1,
                '&:hover': !isDisabled ? {
                  elevation: 4,
                  transform: 'translateY(-4px)',
                  '& .module-icon': { transform: 'scale(1.1)' }
                } : {}
              }}
              onClick={isDisabled ? undefined : module.onClick}
            >
              <CardContent sx={{
                textAlign: 'center',
                py: isCompact ? 1 : 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1
              }}>
                <Box
                  className="module-icon"
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: module.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    transition: 'transform 0.2s ease-in-out'
                  }}
                >
                  <IconComponent sx={{ fontSize: 32, color: 'white' }} />
                </Box>

                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ color: module.color, fontWeight: 600 }}
                >
                  {module.name}
                </Typography>

                {!isCompact && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {module.description}
                    {isDisabled && (
                      <Box component="span" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.disabled' }}>
                        Coming Soon
                      </Box>
                    )}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

function CAProductsHome({
  onNavigateToModule,
  isReadOnly = false,
  isCompact = false,
  availablePlans = [],
  plansLoading = false,
  selectedFvaPlanId = '',
  onFvaPlanChange,
}) {
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleModuleClick = (moduleId, subModule) => {
    if (onNavigateToModule) {
      onNavigateToModule(moduleId, subModule);
    }
  };

  const coreModules = [
    {
      id: 'outcomes',
      name: 'Outcomes',
      description: 'Manage project outcomes and track progress',
      icon: OutcomesIcon,
      color: '#90caf9',
      onClick: () => handleModuleClick('outcomes'),
    },
    {
      id: 'roster',
      name: 'Roster',
      description: 'Manage team members and assignments',
      icon: RosterIcon,
      color: '#a5d6a7',
      onClick: () => handleModuleClick('roster'),
    },
    {
      id: 'admin',
      name: 'Admin',
      description: 'System administration and settings',
      icon: AdminIcon,
      color: '#d1c4e9',
      onClick: () => handleModuleClick('admin'),
    },
  ];

  const pmToolsModules = [
    {
      id: 'pmmode',
      subModule: 'capacityTracking',
      name: 'Capacity Planner',
      description: 'Plan team capacity, sprints, and velocity',
      icon: CapacityIcon,
      color: '#80cbc4',
      onClick: () => handleModuleClick('pmmode', 'capacityTracking'),
    },
    {
      id: 'pmmode',
      subModule: 'docHub',
      name: 'Doc Hub',
      description: 'Roadmaps, PRDs, Data Maps and other documents',
      icon: FolderIcon,
      color: '#b2dfdb',
      onClick: () => handleModuleClick('pmmode', 'docHub'),
    },
    {
      id: 'pmmode',
      subModule: 'dataMap',
      name: 'Data Map',
      description: 'Map your data sources, flows, and dependencies',
      icon: DataMapIcon,
      color: '#b2dfdb',
      onClick: () => handleModuleClick('pmmode', 'dataMap'),
    },
    {
      id: 'pmmode',
      subModule: 'roadmaps',
      name: 'Roadmaps',
      description: 'Build and share visual product roadmaps',
      icon: RoadmapsIcon,
      color: '#b2dfdb',
      onClick: () => handleModuleClick('pmmode', 'roadmaps'),
    },
  ];

  const analyticsModules = [
    {
      id: 'planning',
      name: 'Planning',
      description: 'Project planning and scheduling tools',
      icon: PlanningIcon,
      color: '#ffcc80',
      onClick: () => handleModuleClick('planning'),
    },
  ];

  const devModules = [
    {
      id: 'regression',
      name: 'Regression',
      description: 'Run backend and E2E test suites',
      icon: TestIcon,
      color: '#b0bec5',
      onClick: () => handleModuleClick('regression'),
    },
  ];

  const handleAddProduct = () => {
    if (isReadOnly) {
      setError('Cannot add products in read-only mode');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">CA Products</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Manage your CA products and configurations
            {isReadOnly && ' (Read-Only Mode)'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddProduct}
          disabled={isReadOnly}
          sx={{
            bgcolor: isReadOnly ? 'action.disabled' : '#90caf9',
            '&:hover': { bgcolor: isReadOnly ? 'action.disabled' : '#5d8fc9' }
          }}
        >
          Add Product
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Core */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader label="Core" />
        <ModuleGrid modules={coreModules} isCompact={isCompact} />
      </Box>

      {/* PM Tools */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader label="PM Tools" />
        <ModuleGrid modules={pmToolsModules} isCompact={isCompact} />
      </Box>

      {/* Analytics */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader label="Leadership" />
        <ModuleGrid modules={analyticsModules} isCompact={isCompact} />

        {/* FVA card with plan pre-selector */}
        <Grid container spacing={isCompact ? 1 : 3} sx={{ mt: 0 }}>
          <Grid item xs={isCompact ? 6 : 12} sm={isCompact ? 4 : 6} md={isCompact ? 2 : 4} lg={isCompact ? 2 : 3}>
            <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.2s ease-in-out' }}>
              <CardContent sx={{ textAlign: 'center', py: isCompact ? 1 : 4, display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <Box
                  className="module-icon"
                  sx={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#f8bbd9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mb: 2, transition: 'transform 0.2s ease-in-out' }}
                >
                  <ForecastIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h6" component="h2" gutterBottom sx={{ color: '#f8bbd9', fontWeight: 600 }}>
                  Forecast vs Actuals
                </Typography>
                {!isCompact && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                    Compare forecasted vs actual performance
                  </Typography>
                )}
                {(availablePlans.length > 0 || plansLoading) && (
                  <FormControl
                    fullWidth
                    size="small"
                    variant="outlined"
                    sx={{ mb: 1.5 }}
                    onClick={e => e.stopPropagation()}
                  >
                    <InputLabel>Plan</InputLabel>
                    <Select
                      value={selectedFvaPlanId}
                      onChange={e => { e.stopPropagation(); onFvaPlanChange?.(e.target.value); }}
                      label="Plan"
                      disabled={plansLoading}
                    >
                      {plansLoading && (
                        <MenuItem value="" disabled>
                          <CircularProgress size={14} sx={{ mr: 1 }} /> Loading…
                        </MenuItem>
                      )}
                      {availablePlans.map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.name} ({p.year || '—'})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleModuleClick('forecast')}
                  sx={{ mt: 'auto', bgcolor: '#f8bbd9', color: 'text.primary', '&:hover': { bgcolor: '#f48fb1' } }}
                >
                  Open
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Developer Tools (collapsed by default) */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={showAdvanced ? 'Hide developer tools' : 'Show developer tools'}>
            <IconButton
              onClick={() => setShowAdvanced(!showAdvanced)}
              size="small"
              sx={{
                transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out'
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Tooltip>

          <DeveloperModeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />

          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: 1.2, cursor: 'pointer' }}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            Developer Tools
          </Typography>

          <Typography variant="body2" color="text.disabled" sx={{ ml: 1 }}>
            ({showAdvanced ? 'Hide' : 'Show'} development and testing utilities)
          </Typography>
        </Box>

        <Collapse in={showAdvanced}>
          <Box sx={{ mt: 2 }}>
            <ModuleGrid modules={devModules} isCompact={isCompact} />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
}

export default CAProductsHome;
